import React, { useEffect, useState } from "react";
import {
  collection,
  limit as qLimit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useVenue } from "../VenueProvider";
import { formatCalendarDate } from "../../utils/dates";
import { conSaltos, describeCambioPrecio } from "../../utils/historialPrecios";

// Línea de tiempo única de los precios acordados (FRA-340). Tarifa de
// servicio, comisión de planner y gasto
// recurrente leen esta misma vista: anterior → nuevo, salto en pesos y en
// porcentaje, tiempo desde el cambio previo, quién lo cambió y su nota.
// El tono del porcentaje asume que el precio es un costo: subir es rojo.
const fmtMoney = (v) =>
  "$" + (Number(v) || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 });

export default function HistorialPrecios({
  pathSegments, // ["venues", venueId, ..., "costoHistorial"]
  titulo,
  labels = {}, // { inicial, eliminado }
  vacioTexto = "Todavía no hay cambios registrados.",
  ocultarVacio = false, // sin registros, no pintar ni el contenedor
  nota = null, // pie de la tarjeta
  limite = 50,
  className = "",
}) {
  const { calendarTimezone } = useVenue();
  const [historial, setHistorial] = useState(null); // null = cargando
  const path = Array.isArray(pathSegments) ? pathSegments : [];
  const pathKey = path.join("/");
  const incompleto = path.length === 0 || path.some((s) => !s);

  useEffect(() => {
    if (incompleto) {
      setHistorial([]);
      return undefined;
    }
    const q = query(
      collection(db, ...pathKey.split("/")),
      orderBy("fecha", "desc"),
      qLimit(limite)
    );
    const unsub = onSnapshot(
      q,
      (snap) => setHistorial(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("HistorialPrecios snapshot error:", err);
        setHistorial([]);
      }
    );
    return unsub;
  }, [pathKey, incompleto, limite]);

  const lista = conSaltos(historial || []);
  if (ocultarVacio && (historial === null || lista.length === 0)) return null;

  return (
    <div
      className={`rounded-xl border border-zinc-100 bg-white dark:bg-zinc-800/30 dark:border-zinc-700/40 p-5 ${className}`}
    >
      {titulo && (
        <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500 mb-4">
          {titulo}
        </div>
      )}
      {historial === null ? (
        <p className="text-sm text-zinc-400">Cargando…</p>
      ) : lista.length === 0 ? (
        <p className="text-sm text-zinc-400">{vacioTexto}</p>
      ) : (
        <ol className="space-y-3">
          {lista.map((h) => (
            <li key={h.id} className="flex gap-3">
              <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-zinc-300" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  {describeCambioPrecio(h, labels, fmtMoney)}
                  {h.saltoPct !== null && h.salto !== 0 && (
                    <span
                      className={`ml-2 text-xs font-semibold ${
                        h.salto > 0 ? "text-red-600" : "text-emerald-600"
                      }`}
                    >
                      {h.salto > 0 ? "+" : "−"}
                      {fmtMoney(Math.abs(h.salto))} ({h.saltoPct > 0 ? "+" : ""}
                      {h.saltoPct.toFixed(h.saltoPct % 1 ? 1 : 0)}%)
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500">
                  {formatCalendarDate(h.fecha, calendarTimezone, {
                    style: "short",
                  })}
                  {h.mesesDesdeAnterior !== null &&
                    h.mesesDesdeAnterior > 0 &&
                    ` · ${h.mesesDesdeAnterior} ${
                      h.mesesDesdeAnterior === 1 ? "mes" : "meses"
                    } después del cambio anterior`}
                  {h.setByName ? ` · ${h.setByName}` : ""}
                </div>
                {h.notas && (
                  <div className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-300 break-words">
                    {h.notas}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
      {nota && (
        <p className="mt-3 text-xs text-zinc-500 border-t border-zinc-100 dark:border-zinc-700/40 pt-3">
          {nota}
        </p>
      )}
    </div>
  );
}
